import React, { useMemo, useState } from "react";
import axios from "axios";
import { parseApiError } from "../lib/apiError";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Loader2, Lock, AlertCircle, ShieldCheck } from "lucide-react";
import { PhoneField, CountrySelect } from "./ContactInputs";
import { DEFAULT_DIAL, DEFAULT_COUNTRY } from "../lib/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialBuyer = {
  first_name: "", last_name: "", email: "",
  phone_dial: DEFAULT_DIAL, phone_number: "",
  address: "", country: DEFAULT_COUNTRY,
};

export default function CheckoutDetailsModal({ open, onClose, profileId }) {
  const [buyer, setBuyer] = useState(initialBuyer);
  const [attempted, setAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setBuyer((s) => ({ ...s, [k]: v }));

  const validation = useMemo(() => {
    const e = {};
    if (!buyer.first_name.trim()) e.first_name = "First name is required.";
    if (!buyer.last_name.trim()) e.last_name = "Last name is required.";
    if (!buyer.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyer.email)) e.email = "Please enter a valid email.";
    if (!buyer.phone_number.trim()) e.phone = "Phone number is required.";
    if (!buyer.address.trim()) e.address = "Address is required.";
    if (!buyer.country) e.country = "Please select your country.";
    return e;
  }, [buyer]);
  const errors = attempted ? validation : {};
  const canProceed = Object.keys(validation).length === 0;

  const submit = async () => {
    if (!canProceed) { setAttempted(true); return; }
    setLoading(true); setErr("");
    try {
      const r = await axios.post(`${API}/profile/${profileId}/unlock-checkout`, {
        origin_url: window.location.origin,
        buyer: {
          first_name: buyer.first_name.trim(),
          last_name: buyer.last_name.trim(),
          email: buyer.email.trim(),
          phone: `${buyer.phone_dial} ${buyer.phone_number.trim()}`,
          address: buyer.address.trim(),
          country: buyer.country,
        },
      });
      if (r.data?.already_unlocked) { window.location.reload(); return; }
      if (r.data?.url) { window.location.href = r.data.url; return; }
      throw new Error("Checkout failed");
    } catch (e) {
      setErr(parseApiError(e));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !loading) onClose(); }}>
      <DialogContent
        data-testid="checkout-details-modal"
        className="max-w-lg bg-[#0B0E14] border border-[#D4AF37]/20 text-white p-0 overflow-hidden"
      >
        <DialogHeader className="px-7 pt-7 pb-1">
          <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">secure checkout</p>
          <DialogTitle className="font-serif text-2xl text-white mt-1">Your details</DialogTitle>
          <DialogDescription className="font-sans text-sm text-slate-400 mt-1">
            Required for your receipt and lifetime access to your blueprint.
          </DialogDescription>
        </DialogHeader>

        <div className="px-7 pb-2 max-h-[62vh] overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" required value={buyer.first_name} onChange={(v) => set("first_name", v)} testid="buyer-first-name" error={errors.first_name} errorKey="first_name" />
            <Field label="Last name" required value={buyer.last_name} onChange={(v) => set("last_name", v)} testid="buyer-last-name" error={errors.last_name} errorKey="last_name" />
          </div>
          <Field label="Email" required type="email" value={buyer.email} onChange={(v) => set("email", v)} testid="buyer-email" error={errors.email} errorKey="email" />
          <PhoneField
            required
            dial={buyer.phone_dial}
            onDialChange={(v) => set("phone_dial", v)}
            value={buyer.phone_number}
            onChange={(v) => set("phone_number", v)}
            testid="buyer-phone"
            error={errors.phone}
            errorKey="phone"
          />
          <Field label="Address" required value={buyer.address} onChange={(v) => set("address", v)} testid="buyer-address" error={errors.address} errorKey="address" placeholder="Street, city, postcode" />
          <CountrySelect required value={buyer.country} onChange={(v) => set("country", v)} testid="buyer-country" error={errors.country} errorKey="country" />
          {err && (
            <p className="text-sm text-red-300/90 font-sans flex items-center gap-1.5" data-testid="buyer-error">
              <AlertCircle className="w-3.5 h-3.5" /> {err}
            </p>
          )}
        </div>

        <div className="px-7 py-5 border-t border-white/5 bg-[#040508]/60">
          <button
            data-testid="buyer-continue-btn"
            onClick={submit}
            disabled={loading}
            className="btn-gold rounded-full w-full py-3.5 text-xs flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</>
              : <><Lock className="w-4 h-4" /> Continue to payment · $44.44 AUD</>}
          </button>
          <p className="mt-3 text-[11px] text-slate-400 font-sans flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-[#D4AF37]" /> Secured by Stripe · Your details stay private
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, type = "text", value, onChange, testid, required, error, errorKey, placeholder }) {
  const hasError = !!error;
  return (
    <label className="block" data-error-key={errorKey}>
      <p className="font-sans text-[10px] uppercase tracking-overline text-slate-400 mb-2">
        {label}{required && <span className="text-[#D4AF37] ml-1">*</span>}
      </p>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={hasError}
        className={`cosmic-input rounded-xl px-4 py-3 w-full font-sans text-base transition-colors ${
          hasError ? "!border-red-400/80 focus:!border-red-400 !bg-red-500/[0.04]" : ""
        }`}
      />
      {hasError && (
        <p className="mt-1.5 text-xs text-red-300/90 font-sans flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
    </label>
  );
}
